/**
 * Voice cues — a rising "tung" when listening starts and a falling "tong" when it
 * ends, like the native voice card. Synthesized with Web Audio (no asset files) on the
 * page's ONE shared AudioContext (./shared-audio-context), so a cue never pays for a
 * device init of its own. The tone is still never transcribed: what goes up the Line is
 * the mic track on the peer connection, and the context only ever TAPS that track for an
 * analyser — a cue reaches the speakers and nothing else (and the mic's echoCancellation
 * suppresses any acoustic bleed). Fully degrade-safe: a no-op when Web Audio is
 * unavailable (e.g. jsdom in tests).
 */
import { getSharedAudioContext } from "./shared-audio-context";

/** A provider of an alternative RUNNING context (the capture graph registers one).
 *  If our own context can't run in time, the cue plays through it instead —
 *  a scheduled-but-suspended cue is a SILENT cue, and the moment we play the
 *  start cue the capture context is running by definition. */
let fallbackCtx: (() => AudioContext | null) | null = null;
export function registerEarconFallbackCtx(
  provider: () => AudioContext | null
): void {
  fallbackCtx = provider;
}

/**
 * Resume the cue context. Call from a user gesture (the hold-B keydown) so the first
 * cue isn't dropped by a suspended context.
 */
export function primeEarcons(): void {
  const c = getSharedAudioContext();
  if (c && c.state === "suspended") void c.resume().catch(() => {});
}

/**
 * ROUTE KEEP-ALIVE — the native app's keepalive.rs, ported (founder 08-05:
 * "learn from native"). A true-silence source holds this context's OUTPUT
 * stream genuinely running for the app's lifetime, so a Bluetooth output
 * (AirPods) never drops its A2DP route to low power between holds — the
 * cold-wake of an idle route is what mutes ~1s of output (swallowing the
 * start cue) and it is why Electron dictation apps feel smooth incidentally.
 * OUTPUT-only: it never opens a mic, so the mic-indicator invariant is
 * untouched, and silence never trips the tab's audible badge.
 *
 * A context can only run after the page has user activation; until then the
 * first real interaction (pointerdown/keydown) resumes it, one-shot.
 */
let keepaliveStarted = false;
export function startRouteKeepalive(): void {
  if (keepaliveStarted) return;
  const c = getSharedAudioContext();
  if (!c) return;
  try {
    const src = c.createConstantSource();
    src.offset.value = 0; // true silence — the point is a RUNNING output stream
    src.connect(c.destination);
    src.start();
  } catch {
    return; // no ConstantSourceNode → keep-alive unavailable, cues still work
  }
  keepaliveStarted = true;
  if (c.state === "running") return;
  void c.resume().catch(() => {});
  const onActivation = () => void c.resume().catch(() => {});
  const settle = () => {
    if (c.state !== "running") return;
    window.removeEventListener("pointerdown", onActivation, true);
    window.removeEventListener("keydown", onActivation, true);
    c.removeEventListener("statechange", settle);
  };
  c.addEventListener("statechange", settle);
  window.addEventListener("pointerdown", onActivation, true);
  window.addEventListener("keydown", onActivation, true);
}

interface Note {
  freq: number;
  at: number;
  dur: number;
  gain?: number;
}

function play(notes: Note[]): void {
  let c = getSharedAudioContext();
  if (c && c.state !== "running") {
    const fb = fallbackCtx?.();
    if (fb && fb.state === "running") c = fb;
  }
  if (!c) return;
  const schedule = () => {
    const base = c.currentTime + 0.02;
    for (const n of notes) {
      const osc = c.createOscillator();
      const g = c.createGain();
      osc.type = "sine";
      osc.frequency.value = n.freq;
      const start = base + n.at;
      const peak = n.gain ?? 0.11;
      g.gain.setValueAtTime(0.0001, start);
      g.gain.exponentialRampToValueAtTime(peak, start + 0.012);
      g.gain.exponentialRampToValueAtTime(0.0001, start + n.dur);
      osc.connect(g).connect(c.destination);
      osc.start(start);
      osc.stop(start + n.dur + 0.03);
    }
  };
  try {
    if (c.state === "running") schedule();
    else
      void c
        .resume()
        .then(schedule)
        .catch(() => {});
  } catch {
    // ignore — a dropped cue must never break dictation
  }
}

/** Rising cue — listening started, speak now. */
export function playStartCue(): void {
  play([
    { freq: 587, at: 0, dur: 0.08 },
    { freq: 784, at: 0.065, dur: 0.12 },
  ]);
}

/** Falling cue — listening done. */
export function playStopCue(): void {
  play([
    { freq: 784, at: 0, dur: 0.08 },
    { freq: 523, at: 0.065, dur: 0.13 },
  ]);
}
